import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import AdvancedRouterModal from '../components/AdvancedRouterModal';
import HeroSignalBeam from '../components/HeroSignalBeam';
import HomeAddressCheck from '../components/HomeAddressCheck';
import HomeKingmanPlans from '../components/HomeKingmanPlans';
import { LeadContext } from '../utils/leadContext';

/** Add more files under public/images/hero/ and append paths here to rotate. */
const HERO_BACKGROUNDS = [
  `${process.env.PUBLIC_URL}/images/hero/bk-01.png?v=10`,
];
const HERO_ROTATE_MS = 8000;

const Home: React.FC = () => {
  const [routerModalOpen, setRouterModalOpen] = useState(false);
  const [lead, setLead] = useState<LeadContext | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const [showAddressCheck, setShowAddressCheck] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    HERO_BACKGROUNDS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (HERO_BACKGROUNDS.length < 2) return;
    const id = window.setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_BACKGROUNDS.length);
    }, HERO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const focusAddressCheck = () => {
    setShowAddressCheck((open) => {
      if (open) return false;

      const title = document.getElementById('home-hero-title');
      const before = title?.getBoundingClientRect().top ?? 0;

      window.setTimeout(() => {
        const after = title?.getBoundingClientRect().top ?? before;
        const delta = after - before;
        if (Math.abs(delta) > 1) {
          // Keep titles visually fixed while the panel opens downward
          window.scrollBy({ top: -delta, behavior: 'auto' });
        }
        const input = document.getElementById('home-address-input') as HTMLInputElement | null;
        input?.focus({ preventScroll: true });
      }, 20);

      return true;
    });
  };

  const features = [
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Experience blazing-fast internet speeds with our cutting-edge technology',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Transparent Pricing',
      description: 'Simple, straightforward pricing with no hidden fees.',
    },
    {
      icon: UserGroupIcon,
      title: 'Local Support',
      description: 'Arizona-based support — phone Mon–Fri 9 AM–5 PM (AZ), plus a 24/7 Online Support Center',
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Easy Installation',
      description: 'Professional installation team gets you connected quickly and efficiently',
    },
  ];

  const perks = ['Free installation', 'No contracts'];

  const SectionHairline = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-hidden="true">
      <div className="border-t border-white/25" />
    </div>
  );

  return (
    <div className="relative min-h-screen">
      {/* Fixed background — stays while all page content scrolls up over it */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[#0a0f1a]" aria-hidden="true">
        {HERO_BACKGROUNDS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1600ms] ease-in-out"
            style={{ opacity: i === heroIndex ? 1 : 0 }}
          />
        ))}
        <HeroSignalBeam />
      </div>

      {/* Everything scrolls: hero copy + rest of site */}
      <div className="relative z-10">
      {/* Hero copy — grows with content so nothing overlaps the divider */}
      <section className="flex flex-col justify-start pt-24 pb-12 md:min-h-[100svh] md:justify-center md:pt-32 md:pb-24">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-xl md:max-w-2xl text-left"
          >
            <h1
              id="home-hero-title"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.05]"
            >
              Fast, local internet.
            </h1>
            <p className="mt-4 md:mt-5 text-lg md:text-xl text-white/85 max-w-lg leading-relaxed">
              Reliable high-speed internet for Kingman &amp; Golden Valley.
            </p>

            <div className="mt-8 md:mt-10 flex items-center gap-5 sm:gap-6">
              <div className="shrink-0">
                <p className="text-xs md:text-sm font-bold tracking-[0.18em] uppercase text-blue-400">
                  Starting at
                </p>
                <p className="mt-1 flex items-start text-white font-extrabold leading-none">
                  <span className="text-5xl md:text-6xl">$39</span>
                  <span className="ml-0.5 mt-1 text-2xl md:text-3xl font-bold">.99</span>
                  <span className="ml-2 mt-3 text-lg md:text-xl font-semibold text-white/70">/mo</span>
                </p>
              </div>

              <div className="self-stretch w-px bg-white/35" aria-hidden="true" />

              <ul className="space-y-1 text-sm sm:text-base text-white/85 leading-snug">
                {perks.map((perk) => (
                  <li key={perk}>{perk}</li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={focusAddressCheck}
                  aria-expanded={showAddressCheck}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-base px-7 py-3.5 transition-colors"
                >
                  Check Availability
                  {showAddressCheck ? (
                    <ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
                <a
                  href="#home-plans"
                  className="inline-flex items-center justify-center gap-2 text-white font-semibold text-base px-3 py-3.5 hover:text-blue-300 transition-colors"
                >
                  View Plans
                  <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
                </a>
              </div>

              <div
                id="home-address-panel"
                className={`w-full max-w-xl transition-[margin] duration-150 ${
                  showAddressCheck ? 'mt-4' : 'mt-0'
                }`}
              >
                <HomeAddressCheck
                  stayOnPage
                  onAvailable={setLead}
                  variant="dark"
                  revealed={showAddressCheck}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <SectionHairline />

      {/* Features Section */}
      <section className="py-12 md:py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">Why Choose Airmax Internet?</h2>
            <p className="text-xl text-white/85 max-w-3xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              We're committed to providing the best internet experience with cutting-edge technology and exceptional
              service.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-blue-600 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">{feature.title}</h3>
                <p className="text-white/80 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SectionHairline />

      {/* Gamers Banner */}
      <section className="py-12 md:py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex-1"
            >
              <span className="inline-block bg-blue-500/15 border border-blue-400/30 text-blue-300 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                New
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
                Wave Fiber Speeds.
                <br />
                <span className="text-blue-400">Built for Gamers.</span>
              </h2>
              <p className="text-white/85 text-lg max-w-xl mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                Low latency, dedicated bandwidth, and consistent performance — everything competitive gamers need. See
                why Airmax is the gamer's choice.
              </p>
              <Link
                to="/gamers"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-500 transition-all"
              >
                Learn More
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex-1 w-full max-w-md lg:max-w-lg"
            >
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
                <video
                  className="w-full block"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  style={{ objectFit: 'cover', width: '100%' }}
                >
                  <source src="/videos/4.mp4" type="video/mp4" />
                </video>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold text-lg">Fast Speeds</p>
                  <p className="text-blue-300 text-sm">Up to 1 Gbps Symmetrical</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SectionHairline />

      <HomeKingmanPlans lead={lead} transparentBg />

      <SectionHairline />

      {/* Google Reviews */}
      <section className="py-12 md:py-16 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">What Our Customers Say</h2>
          </div>
          <div className="elfsight-app-17db52aa-5b00-4a56-ac65-95deee92a12a overflow-hidden" data-elfsight-app-lazy></div>
        </div>
      </section>
      </div>

      <AdvancedRouterModal isOpen={routerModalOpen} onClose={() => setRouterModalOpen(false)} />
    </div>
  );
};

export default Home;
